// ── Lista de Vidas: a vida como o Hub a devolve (núcleo puro) ─────
// O Hub (api-bluehub) é a fonte de verdade das vidas. Este arquivo lê a
// resposta de GET /integrations/crm/empresas/{id}/vidas, normaliza os valores
// para comparação e responde perguntas sobre a leitura (vaga livre,
// dependentes, quem tem o CPF ou o e-mail, que operação cabe numa vida).
//
// Sem I/O, sem React, sem alias `@`, sem `Date.now()`: o "hoje" entra por
// parâmetro. O servidor (CommonJS) espelha TUDO o que é exportado aqui em
// server/lista-vidas/dominio.js, com os mesmos nomes; a paridade é conferida
// por paridadeServidor.test.ts. Mudou aqui, muda lá.

// ── Domínios ──────────────────────────────────────────────────────

export const TIPOS_OPERACAO = [
  'alterar_dados', 'trocar_email', 'adicionar_vida',
  'ativar_vida', 'desfazer_convite', 'remover_vaga',
] as const;
export type TipoOperacao = typeof TIPOS_OPERACAO[number];

export type CampoDados = 'nome_completo' | 'cpf' | 'data_nascimento' | 'nome_da_mae' | 'telefone';
export const CAMPOS_ALTERAVEIS: readonly CampoDados[] =
  ['nome_completo', 'cpf', 'data_nascimento', 'nome_da_mae', 'telefone'];
export const ADICIONAIS_PERMITIDOS = ['clin_odonto', 'zenklub_psi'] as const;

/** Tetos do Hub por empresa em 24h (informativo na tela; quem recusa é o Hub com 429). */
export const LIMITES_24H: Readonly<Record<TipoOperacao, number>> = {
  alterar_dados: 300, trocar_email: 100, adicionar_vida: 60,
  ativar_vida: 300, desfazer_convite: 100, remover_vaga: 50,
};

/** Uma vaga como a leitura GET /integrations/crm/empresas/{id}/vidas devolve (já parseada). */
export interface VidaHub {
  seatPublicId: string;                       // uuid minúsculo
  nome: string | null;
  email: string | null;
  telefone: string | null;                    // CRU: não é E.164 garantido
  cpf: string | null;                         // a chave tem allow_cpf
  sexo: 'M' | 'F' | null;
  sexoOrigem: 'informado' | 'inferido_pelo_nome' | null;
  plano: { nome: string | null; slug: string | null; interval: string | null } | null;
  adicionais: string[];
  seatStatus: string;                         // 'vacant'|'invited'|'assigned'|'canceled' + enum ABERTO
  ativo: boolean;
  tipo: 'titular' | 'dependente' | null;      // chave opcional
  titularSeatPublicId: string | null;
  parentesco: string | null;
  dataNascimento: string | null;              // AAAA-MM-DD
  nomeDaMae: string | null;
}

export interface LeituraVidas {
  total: number;
  vidas: VidaHub[];
  /** A resposta não está completa: `items.length < total`, `page != null`,
   *  ou algum item não pôde ser lido (sem `seat_public_id`). */
  incompleta: boolean;
  /** false se há vida com pessoa e nenhuma tem CPF de 11 dígitos (chave sem allow_cpf). */
  cpfVisivel: boolean;
}

// ── Normalização (comparação) ─────────────────────────────────────

export const soDigitos = (x: unknown): string => String(x ?? '').replace(/\D/g, '');

/** 11 dígitos, ou '' quando não é um CPF completo (inclusive mascarado). */
export function normalizarCpf(x: unknown): string {
  const d = soDigitos(x);
  return d.length === 11 ? d : '';
}

/** Só dígitos; com 12 ou 13 dígitos começando em 55, tira o DDI. */
export function normalizarTelefone(x: unknown): string {
  const d = soDigitos(x);
  return (d.length === 12 || d.length === 13) && d.startsWith('55') ? d.slice(2) : d;
}

export const normalizarEmail = (x: unknown): string => String(x ?? '').trim().toLowerCase();

/** NFC, sem espaço nas pontas, espaços colapsados, sem caixa. Acento mantido. */
export const normalizarNome = (x: unknown): string =>
  String(x ?? '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

/** `AAAA-MM-DD` de calendário real devolve o próprio valor; senão ''. */
export function normalizarData(x: unknown): string {
  const s = typeof x === 'string' ? x.trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  const t = Date.parse(`${s}T00:00:00Z`);
  if (Number.isNaN(t)) return '';
  return new Date(t).toISOString().slice(0, 10) === s ? s : '';
}

const SEXO_M = ['m', 'masculino', 'masc.'];
const SEXO_F = ['f', 'feminino', 'fem.'];

/** 'M' | 'F'; vazio ou ausente vira null; outro valor é inválido (undefined). */
export function normalizarSexo(x: unknown): 'M' | 'F' | null | undefined {
  if (x === null || x === undefined) return null;
  const s = String(x).trim().toLowerCase();
  if (s === '') return null;
  if (SEXO_M.includes(s)) return 'M';
  if (SEXO_F.includes(s)) return 'F';
  return undefined;
}

/** `***.***.***-DD` (só os 2 últimos). Vazio/null vira null; outro valor, '***'. */
export function mascararCpf(x: unknown): string | null {
  const s = String(x ?? '').trim();
  if (!s) return null;
  const d = soDigitos(s);
  return d.length === 11 ? `***.***.***-${d.slice(-2)}` : '***';
}

/** Telefone mascarado: `****` + os 4 últimos dígitos (a mesma regra de
 *  `lista_vidas.fn_expurgar_pii`); com menos de 4 dígitos, '***'. Vazio vira null. */
function mascararTelefone(x: unknown): string | null {
  const s = String(x ?? '').trim();
  if (!s) return null;
  const d = soDigitos(s);
  return d.length >= 4 ? `****${d.slice(-4)}` : '***';
}

/**
 * PII de `antes`/`depois` mascarada para listagem (adendo D3; as regras de
 * `lista_vidas.fn_expurgar_pii`, que o banco aplica ao fechar): cópia rasa;
 * `cpf`/`titular_cpf` passam por `mascararCpf`; `telefone` fica só com os 4
 * últimos dígitos; `data_nascimento` e `nome_da_mae` saem. Nome e e-mail
 * ficam (auditoria de quem foi afetado). O que não é objeto volta como veio.
 */
export function mascararPii(obj: Record<string, unknown>): Record<string, unknown>;
export function mascararPii(obj: Record<string, unknown> | null): Record<string, unknown> | null;
export function mascararPii(obj: unknown): unknown;
export function mascararPii(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out: Record<string, unknown> = { ...(obj as Record<string, unknown>) };
  delete out.data_nascimento;
  delete out.nome_da_mae;
  for (const k of ['cpf', 'titular_cpf']) {
    if (Object.prototype.hasOwnProperty.call(out, k)) out[k] = mascararCpf(out[k]);
  }
  if (Object.prototype.hasOwnProperty.call(out, 'telefone')) out.telefone = mascararTelefone(out.telefone);
  return out;
}

/** DV do CPF (a mesma regra de server/bluehub/validacao.js). */
export function cpfValido(x: unknown): boolean {
  const cpf = soDigitos(x);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const dv = (pesoInicial: number): number => {
    let soma = 0;
    for (let i = 0; i < pesoInicial - 1; i += 1) soma += Number(cpf[i]) * (pesoInicial - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return dv(10) === Number(cpf[9]) && dv(11) === Number(cpf[10]);
}

/** Forma do e-mail como o Hub confere: um @, texto dos dois lados, ponto no domínio. */
export const emailValido = (x: unknown): boolean =>
  /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(String(x ?? '').trim());

/** Só dígitos e pontuação de telefone; de 10 a 13 dígitos. */
export function telefoneValido(x: unknown): boolean {
  const s = String(x ?? '').trim();
  return /^[\d\s()+.-]+$/.test(s) && /^\d{10,13}$/.test(soDigitos(s));
}

/** Data real e não depois de `hojeIso`. `hojeIso` inválido: false (sem relógio, não prova). */
export function dataValida(d: unknown, hojeIso: string): boolean {
  const n = normalizarData(d);
  const h = normalizarData(hojeIso);
  return n !== '' && h !== '' && n <= h;
}

/** "Hoje" em Brasília (UTC-3, sem horário de verão desde 2019) a partir de um instante. */
export const hojeIsoBrasilia = (agoraMs: number): string =>
  new Date(agoraMs - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);

/** Valor de um campo editável como a comparação o enxerga. */
export function normalizarCampoDados(campo: CampoDados, valor: unknown): string {
  switch (campo) {
    case 'nome_completo':
    case 'nome_da_mae':
      return normalizarNome(valor);
    case 'cpf':
      return normalizarCpf(valor);
    case 'data_nascimento':
      return normalizarData(valor);
    case 'telefone':
      return normalizarTelefone(valor);
  }
}

/** O valor atual (cru, como veio do Hub) de um campo editável da vida. */
export function valorDoCampo(vida: VidaHub, campo: CampoDados): string | null {
  switch (campo) {
    case 'nome_completo': return vida.nome;
    case 'cpf': return vida.cpf;
    case 'data_nascimento': return vida.dataNascimento;
    case 'nome_da_mae': return vida.nomeDaMae;
    case 'telefone': return vida.telefone;
  }
}

// ── Leitura ───────────────────────────────────────────────────────

const textoOuNull = (v: unknown): string | null =>
  (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

const idOuNull = (v: unknown): string | null => {
  const t = textoOuNull(v);
  return t ? t.toLowerCase() : null;
};

function lerPlano(p: unknown): VidaHub['plano'] {
  if (typeof p === 'string') {
    const slug = textoOuNull(p);
    return slug ? { nome: null, slug, interval: null } : null;
  }
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  return { nome: textoOuNull(o.nome), slug: textoOuNull(o.slug), interval: textoOuNull(o.interval) };
}

function lerAdicionais(a: unknown): string[] {
  if (!Array.isArray(a)) return [];
  const out: string[] = [];
  for (const x of a) {
    let codigo: string | null = null;
    if (typeof x === 'string') codigo = textoOuNull(x);
    else if (x && typeof x === 'object') {
      const o = x as Record<string, unknown>;
      codigo = textoOuNull(o.adicional) ?? textoOuNull(o.slug) ?? textoOuNull(o.addon_slug);
    }
    if (codigo) out.push(codigo);
  }
  return out;
}

/** Um item da leitura (snake_case). Item sem `seat_public_id` devolve null. */
export function parseVidaHub(item: unknown): VidaHub | null {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const v = item as Record<string, unknown>;
  const seatPublicId = idOuNull(v.seat_public_id);
  if (!seatPublicId) return null;
  const sexo = normalizarSexo(v.sexo);
  const sexoOrigem = v.sexo_origem === 'informado' || v.sexo_origem === 'inferido_pelo_nome'
    ? v.sexo_origem : null;
  const tipo = v.tipo === 'titular' || v.tipo === 'dependente' ? v.tipo : null;
  return {
    seatPublicId,
    nome: textoOuNull(v.nome) ?? textoOuNull(v.nome_completo),
    email: textoOuNull(v.email),
    telefone: textoOuNull(v.telefone),
    cpf: textoOuNull(v.cpf),
    sexo: sexo === undefined ? null : sexo,
    sexoOrigem,
    plano: lerPlano(v.plano),
    adicionais: lerAdicionais(v.adicionais),
    seatStatus: textoOuNull(v.seat_status) ?? textoOuNull(v.status) ?? '',
    ativo: v.ativo === true,
    tipo,
    titularSeatPublicId: idOuNull(v.titular_seat_public_id),
    parentesco: textoOuNull(v.parentesco),
    dataNascimento: normalizarData(v.data_nascimento) || null,
    nomeDaMae: textoOuNull(v.nome_da_mae),
  };
}

/** Há nome, e-mail, CPF ou nascimento. */
export const temPessoa = (v: VidaHub): boolean =>
  Boolean(v.nome || v.email || v.cpf || v.dataNascimento);

export const estaCancelada = (v: VidaHub): boolean => v.seatStatus === 'canceled';

/**
 * Corpo da leitura. Devolve null quando o corpo não tem `items` em lista
 * (resposta que não é a leitura): o servidor trata como leitura que falhou.
 */
export function parseLeituraVidas(corpo: unknown): LeituraVidas | null {
  if (!corpo || typeof corpo !== 'object' || Array.isArray(corpo)) return null;
  const c = corpo as Record<string, unknown>;
  if (!Array.isArray(c.items)) return null;
  const items = c.items as unknown[];
  const vidas: VidaHub[] = [];
  for (const item of items) {
    const v = parseVidaHub(item);
    if (v) vidas.push(v);
  }
  const total = typeof c.total === 'number' && Number.isInteger(c.total) && c.total >= 0
    ? c.total : items.length;
  const incompleta = items.length < total
    || (c.page !== null && c.page !== undefined)
    || vidas.length < items.length;
  const comPessoa = vidas.filter(temPessoa);
  const cpfVisivel = comPessoa.length === 0 || comPessoa.some((v) => normalizarCpf(v.cpf) !== '');
  return { total, vidas, incompleta, cpfVisivel };
}

// ── Perguntas sobre a leitura ─────────────────────────────────────

/** Vaga livre do plano: `vacant`, sem pessoa, não dependente, com o slug pedido. */
export function vagaLivreDoPlano(v: VidaHub, slug: string): boolean {
  return v.seatStatus === 'vacant' && !temPessoa(v) && v.tipo !== 'dependente'
    && !!slug && v.plano?.slug === slug;
}

/** Quantas vagas livres há por slug de plano (só planos com slug). */
export function livresPorPlano(vidas: readonly VidaHub[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const v of vidas) {
    const slug = v.plano?.slug;
    if (slug && vagaLivreDoPlano(v, slug)) out[slug] = (out[slug] ?? 0) + 1;
  }
  return out;
}

/** Dependentes vivos (não cancelados) cuja vaga de titular é `seat`. */
export function dependentesDe(vidas: readonly VidaHub[], seat: string): VidaHub[] {
  const alvo = String(seat ?? '').trim().toLowerCase();
  if (!alvo) return [];
  return vidas.filter((v) => !estaCancelada(v) && v.titularSeatPublicId === alvo);
}

export function vidaPorSeat(vidas: readonly VidaHub[], seat: string): VidaHub | null {
  const alvo = String(seat ?? '').trim().toLowerCase();
  if (!alvo) return null;
  return vidas.find((v) => v.seatPublicId === alvo) ?? null;
}

/** Vagas vivas (não canceladas) com o CPF. CPF incompleto não casa com nada. */
export function vidasComCpf(vidas: readonly VidaHub[], cpf: string): VidaHub[] {
  const alvo = normalizarCpf(cpf);
  if (!alvo) return [];
  return vidas.filter((v) => !estaCancelada(v) && normalizarCpf(v.cpf) === alvo);
}

/** A primeira vaga viva (não cancelada) com o e-mail, sem caixa. */
export function vidaComEmail(vidas: readonly VidaHub[], email: string): VidaHub | null {
  const alvo = normalizarEmail(email);
  if (!alvo) return null;
  return vidas.find((v) => !estaCancelada(v) && normalizarEmail(v.email) === alvo) ?? null;
}

// ── Remoções já aplicadas aguardando o ciclo (adendo D1) ──────────

/**
 * Depois de `remover_vaga` aplicada, a vaga continua visível no GET do Hub
 * até o próximo ciclo. O servidor monta isto a partir de
 * `lista_vidas.solicitacoes` (remover_vaga `aplicada` da mesma empresa na
 * janela `JANELA_REMOCAO_RECENTE_DIAS`, excluindo a própria solicitação na
 * aplicação):
 *   seats    seat_public_id das remoções por e-mail;
 *   porPlano quantidade de remoções por plano, por slug.
 */
export interface RemocoesRecentes {
  seats: string[];
  porPlano: Record<string, number>;
}

export interface OpcoesOperacoes {
  remocoesRecentes?: Pick<RemocoesRecentes, 'seats'> | null;
}

/** O seat está entre as remoções recentes (uuid comparado sem caixa). */
function seatJaRemovido(remocoes: Pick<RemocoesRecentes, 'seats'> | null | undefined, seat: string): boolean {
  const alvo = String(seat ?? '').trim().toLowerCase();
  const seats = remocoes && Array.isArray(remocoes.seats) ? remocoes.seats : [];
  return !!alvo && seats.some((s) => String(s ?? '').trim().toLowerCase() === alvo);
}

const STATUS_COM_CONVITE_OU_ACESSO = ['invited', 'assigned'];
const STATUS_REMOVIVEIS = ['invited', 'assigned', 'vacant'];

/**
 * Operações que cabem nesta vida pela leitura (tabela 1.3, só o que depende
 * da vida). `adicionar_vida` e `remover_vaga` por plano não entram: são ações
 * da empresa. Com `opcoes.remocoesRecentes`, `remover_vaga` não aparece para
 * o seat já removido (ainda visível até o ciclo). A validação completa (com
 * os valores pedidos) é `validarSolicitacao`, no servidor.
 */
export function operacoesPossiveis(
  vida: VidaHub,
  vidas: readonly VidaHub[],
  opcoes: OpcoesOperacoes = {},
): TipoOperacao[] {
  if (estaCancelada(vida)) return [];
  const out: TipoOperacao[] = [];
  const temEmail = normalizarEmail(vida.email) !== '';
  const semDependentes = dependentesDe(vidas, vida.seatPublicId).length === 0;
  if (temPessoa(vida)) out.push('alterar_dados');
  if (STATUS_COM_CONVITE_OU_ACESSO.includes(vida.seatStatus) && temEmail) out.push('trocar_email');
  if (vida.seatStatus === 'invited' && temEmail && normalizarNome(vida.nome) !== ''
      && normalizarCpf(vida.cpf) !== '' && normalizarData(vida.dataNascimento) !== '') {
    out.push('ativar_vida');
  }
  if (vida.seatStatus === 'invited' && temEmail && semDependentes) out.push('desfazer_convite');
  if (STATUS_REMOVIVEIS.includes(vida.seatStatus) && temEmail && semDependentes
      && !seatJaRemovido(opcoes?.remocoesRecentes, vida.seatPublicId)) {
    out.push('remover_vaga');
  }
  return out;
}
